// src/components/financials/BankReconciliation.js
// Updated: 02/13/2026 - Added Check Number extraction and Auto-Scan matching

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, Typography, Paper, Grid, Button, TextField, 
  Table, TableBody, TableCell, TableHead, TableRow, 
  Checkbox, Chip, TableContainer, TableSortLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, Divider, Tooltip, CircularProgress, Alert, FormControlLabel, Switch, IconButton,
  Autocomplete
} from '@mui/material';
import { CloudUpload, FileUpload, Link, MergeType, Star, AutoFixHigh, Sort, History, Build, VisibilityOff, CheckCircle, AddCircle, SwapHoriz } from '@mui/icons-material';
import { db } from '../../services/firebase';
import { collection, query, where, getDocs, writeBatch, doc, serverTimestamp, addDoc, orderBy } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Papa from 'papaparse'; 

const BankReconciliation = () => {
  const { userProfile, currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Date Range
  const now = new Date();
  const firstDayPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [startDate, setStartDate] = useState(firstDayPrevMonth.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  // Data State
  const [bankRows, setBankRows] = useState([]); 
  const [bookRows, setBookRows] = useState([]); 
  const [reconciledCount, setReconciledCount] = useState(0); 
  const [hiddenCount, setHiddenCount] = useState(0);
  
  // Metadata State
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Controls
  const [showAll, setShowAll] = useState(false); 

  // Selection State
  const [selectedBankIds, setSelectedBankIds] = useState([]); 
  const [selectedBookIds, setSelectedBookIds] = useState([]);

  // Sorting State
  const [bankSort, setBankSort] = useState({ key: 'date', direction: 'asc' });
  const [bookSort, setBookSort] = useState({ key: 'date', direction: 'asc' }); 

  // Dialog States
  const [suggestionDialogOpen, setSuggestionDialogOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  
  const [addExpenseOpen, setAddExpenseOpen] = useState(false);
  const [newExpense, setNewExpense] = useState({ bankId: '', date: '', desc: '', amount: '', vendorName: '', category: '' });

  // Transfer Dialog State
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferData, setTransferData] = useState({ bankId: '', date: '', amount: '', description: '', targetAccount: '' });

  const churchId = userProfile?.churchId;

  // --- 0. INITIAL LOAD ---
  useEffect(() => {
      if (!churchId) return;
      const fetchMetadata = async () => {
          try {
              const vQ = query(collection(db, 'churches', churchId, 'financials', 'data', 'vendors'), orderBy('name'));
              const vSnap = await getDocs(vQ);
              setVendors(vSnap.docs.map(d => ({ id: d.id, ...d.data() })));

              const cQ = query(collection(db, 'churches', churchId, 'financials', 'data', 'expenseCategories'), orderBy('name'));
              const cSnap = await getDocs(cQ);
              
              if (!cSnap.empty) {
                  setCategories(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
              } else {
                  const bQ = query(collection(db, 'churches', churchId, 'financials', 'data', 'budgets'), orderBy('name'));
                  const bSnap = await getDocs(bQ);
                  setCategories(bSnap.docs.map(d => ({ id: d.id, ...d.data() })));
              }
          } catch (e) {
              console.error("Error fetching metadata:", e);
          }
      };
      fetchMetadata();
  }, [churchId]);

  // --- 1. CSV PARSING ---
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const parsed = results.data.map((row, idx) => {
             const dateVal = row['Date'] || row['date'] || row['Posted Date'] || row['Transaction Date'];
             const desc = row['Full description'] || row['Description'] || row['Memo'] || row['Merchant name'] || 'Bank Transaction';
             let amountStr = row['Amount'] || row['amount'];
             // Extract Check Number from CSV if available
             const checkColumn = row['Check Number'] || row['Check Num'] || row['Check #'] || row['Check'] || '';
             
             if (!dateVal || !amountStr) return null;

             let isNegative = false;
             if (amountStr.toString().includes('(') && amountStr.toString().includes(')')) isNegative = true;

             const cleanStr = amountStr.toString().replace(/[$,() ]/g, '');
             let amount = parseFloat(cleanStr);

             if (isNaN(amount)) return null;
             if (isNegative) amount = -Math.abs(amount);

             const dateObj = new Date(dateVal);
             if (isNaN(dateObj.getTime())) return null;
             
             return {
                 id: `bank-${idx}`,
                 date: dateObj.toISOString().split('T')[0],
                 desc: desc,
                 amount: amount,
                 checkNumber: checkColumn, // Store Check Number
                 matched: false, 
                 alreadyReconciled: false,
                 permanentlyHidden: false
             };
        }).filter(r => r); 

        setBankRows(parsed);
      }
    });
  };

  // --- 2. DATA FETCHING ---
  const fetchBookData = async () => {
    if (!churchId) return;
    setLoading(true);
    try {
      const incQ = query(collection(db, 'churches', churchId, 'financials', 'data', 'transactions'), where('date', '>=', startDate), where('date', '<=', endDate));
      const expQ = query(collection(db, 'churches', churchId, 'financials', 'data', 'expenses'), where('date', '>=', startDate), where('date', '<=', endDate));
      const hiddenQ = query(collection(db, 'churches', churchId, 'financials', 'data', 'hidden_bank_items'), where('date', '>=', startDate), where('date', '<=', endDate));

      const [incSnap, expSnap, hiddenSnap] = await Promise.all([getDocs(incQ), getDocs(expQ), getDocs(hiddenQ)]);

      const validIncStatus = ['posted', 'need_audit', 'need audit'];
      const validExpStatus = ['approved', 'need_audit', 'need audit'];

      const recFilter = showAll ? (d) => true : (d) => d.reconciled !== true;

      const income = incSnap.docs
        .map(d => ({ id: d.id, ...d.data() })) 
        .filter(d => validIncStatus.includes(d.status) && recFilter(d))
        .map(d => ({ 
            id: d.id, 
            date: d.date, 
            desc: d.donorName || 'Donation', 
            amount: Number(d.amount), 
            type: 'income', 
            method: d.method || '-', 
            auditDesc: d.audit_matchedBankDesc, 
            isReconciled: d.reconciled === true,
            checkNumber: '' // Incomes don't map check numbers in this context
        }));

      const expenses = expSnap.docs
        .map(d => ({ id: d.id, ...d.data() })) 
        .filter(d => validExpStatus.includes(d.status) && recFilter(d))
        .map(d => ({ 
            id: d.id, 
            date: d.date, 
            desc: d.payee || d.vendorName || d.description, 
            amount: -Math.abs(Number(d.amount)), 
            type: 'expense', 
            method: d.method || d.paymentMethod || 'Expense', 
            auditDesc: d.audit_matchedBankDesc, 
            isReconciled: d.reconciled === true, 
            isTransfer: d.isTransfer === true,
            checkNumber: d.checkNumber || '' // Pull Check Number from DB
        }));

      setBookRows([...income, ...expenses]);

      // History & Hidden Logic
      const blockMap = {}; 
      const addToMap = (data) => {
          if (data.reconciled === true && data.audit_matchedBankDate && data.audit_matchedBankDesc) {
              const key = `${data.audit_matchedBankDate}|${data.audit_matchedBankDesc}|${Number(data.amount).toFixed(2)}`;
              blockMap[key] = (blockMap[key] || 0) + 1;
          }
      };
      
      incSnap.docs.forEach(d => addToMap(d.data()));
      expSnap.docs.forEach(d => addToMap(d.data()));

      const hiddenSignatures = new Set();
      hiddenSnap.docs.forEach(doc => { 
          const d = doc.data(); 
          hiddenSignatures.add(`${d.date}|${d.desc}|${Number(d.amount).toFixed(2)}`); 
      });

      let autoRecCount = 0;
      let manualHideCount = 0;

      const updatedBankRows = bankRows.map(row => {
          const absAmt = Math.abs(row.amount).toFixed(2);
          const key = `${row.date}|${row.desc}|${row.amount.toFixed(2)}`; 
          const keyAbs = `${row.date}|${row.desc}|${absAmt}`; 

          if (hiddenSignatures.has(key) || hiddenSignatures.has(keyAbs)) {
              manualHideCount++;
              return { ...row, permanentlyHidden: true };
          }
          
          let found = false;
          if (blockMap[key] && blockMap[key] > 0) { blockMap[key]--; found = true; } 
          else if (blockMap[keyAbs] && blockMap[keyAbs] > 0) { blockMap[keyAbs]--; found = true; }

          if (found) {
              autoRecCount++;
              return { ...row, alreadyReconciled: true, matched: true };
          }
          return row;
      });

      setBankRows(updatedBankRows);
      setReconciledCount(autoRecCount);
      setHiddenCount(manualHideCount);

    } catch (e) {
      console.error(e);
      alert("Error: " + e.message);
    }
    setLoading(false);
  };

  // --- 3. MATCHING HELPERS ---
  const normalize = (str) => {
      if (!str) return '';
      return str.toLowerCase().replace(/[0-9]/g, '').replace(/payment|zelle|business|from|id|ref|check|to|online|transfer/g, '').replace(/[^a-z\s]/g, '').trim();
  };

  const isNameMatch = (bankDesc, systemDesc) => {
      const cleanBank = normalize(bankDesc);
      const cleanSystem = normalize(systemDesc);
      const sysParts = cleanSystem.split(' ').filter(p => p.length > 2);
      if (sysParts.length === 0) return false;
      return sysParts.some(part => cleanBank.includes(part));
  };

  const calculateMatchScore = (bankRow, bookRow) => {
      if (!bankRow) return 0;
      let score = 0;
      if (Math.abs(bankRow.amount - bookRow.amount) < 0.01) score += 100;
      if (isNameMatch(bankRow.desc, bookRow.desc)) score += 50;
      const daysDiff = Math.abs((new Date(bankRow.date) - new Date(bookRow.date)) / (1000 * 60 * 60 * 24));
      if (daysDiff <= 4) score += (10 - daysDiff);
      return score;
  };

  // --- 4. INDICATOR HELPER ---
  const getMatchIndicator = (row) => {
      if (selectedBankIds.length !== 1) return null; 
      const bankRow = bankRows.find(r => r.id === selectedBankIds[0]);
      if (!bankRow) return null;
      const score = calculateMatchScore(bankRow, row);
      if (score >= 150) return <Tooltip title="Strong Match"><Star sx={{ color: 'success.main', fontSize: 18 }} /></Tooltip>;
      if (score >= 100) return <Tooltip title="Amount Match"><Star sx={{ color: 'success.light', fontSize: 18 }} /></Tooltip>;
      if (score >= 50) return <Tooltip title="Name Match"><Star sx={{ color: 'warning.main', fontSize: 18 }} /></Tooltip>;
      return null;
  };

  // --- 5. SORTING ---
  const handleSort = (listType, key) => {
      if (listType === 'bank') {
          const isAsc = bankSort.key === key && bankSort.direction === 'asc';
          setBankSort({ key, direction: isAsc ? 'desc' : 'asc' });
      } else {
          const isAsc = bookSort.key === key && bookSort.direction === 'asc';
          setBookSort({ key, direction: isAsc ? 'desc' : 'asc' });
      }
  };

  const sortedBankRows = useMemo(() => {
      return [...bankRows].sort((a, b) => {
          const valA = a[bankSort.key];
          const valB = b[bankSort.key];
          if (valA < valB) return bankSort.direction === 'asc' ? -1 : 1;
          if (valA > valB) return bankSort.direction === 'asc' ? 1 : -1;
          return 0;
      });
  }, [bankRows, bankSort]);

  const sortedBookRows = useMemo(() => {
      if (bookSort.key === 'smart' && selectedBankIds.length === 1) {
          const bankRow = bankRows.find(r => r.id === selectedBankIds[0]);
          if (bankRow) {
              return [...bookRows].sort((a, b) => {
                  const scoreA = calculateMatchScore(bankRow, a);
                  const scoreB = calculateMatchScore(bankRow, b);
                  return scoreB - scoreA; 
              });
          }
      }
      return [...bookRows].sort((a, b) => {
          let valA = a[bookSort.key];
          let valB = b[bookSort.key];
          if (typeof valA === 'string') valA = valA.toLowerCase();
          if (typeof valB === 'string') valB = valB.toLowerCase();
          if (valA < valB) return bookSort.direction === 'asc' ? -1 : 1;
          if (valA > valB) return bookSort.direction === 'asc' ? 1 : -1;
          return 0;
      });
  }, [bookRows, bookSort, selectedBankIds, bankRows]);

  // --- 6. ACTIONS & HANDLERS ---
  const handleBankRowClick = (bankId) => {
      if (selectedBankIds.includes(bankId)) {
          const newIds = selectedBankIds.filter(id => id !== bankId);
          setSelectedBankIds(newIds);
          if (newIds.length !== 1) setBookSort({ key: 'date', direction: 'asc' }); 
      } else {
          const newIds = [...selectedBankIds, bankId];
          setSelectedBankIds(newIds);
          if (newIds.length === 1) setBookSort({ key: 'smart', direction: 'desc' });
      }
  };

  const toggleBookRow = (id) => {
      if (selectedBookIds.includes(id)) setSelectedBookIds(selectedBookIds.filter(i => i !== id));
      else setSelectedBookIds([...selectedBookIds, id]);
  };

  const hideBankRow = async (e, rowId) => {
      e.stopPropagation();
      if (!window.confirm("Permanently hide this row from future reconciliations?")) return;
      const rowToHide = bankRows.find(r => r.id === rowId);
      if (!rowToHide) return;
      try {
          await addDoc(collection(db, 'churches', churchId, 'financials', 'data', 'hidden_bank_items'), {
              date: rowToHide.date, desc: rowToHide.desc, amount: rowToHide.amount, hiddenAt: serverTimestamp(), hiddenBy: currentUser.uid, reason: 'manual_hide'
          });
          setBankRows(prev => prev.map(r => r.id === rowId ? { ...r, permanentlyHidden: true } : r));
          if (selectedBankIds.includes(rowId)) setSelectedBankIds(selectedBankIds.filter(id => id !== rowId));
          setHiddenCount(prev => prev + 1);
      } catch (err) { alert("Failed to save hide status: " + err.message); }
  };

  // --- 7. QUICK ADD EXPENSE ---
  const openAddExpenseDialog = (e, bankRow) => {
      e.stopPropagation();
      if (bankRow.amount > 0) {
          alert("Quick Add is currently only for Expenses (Negative Amounts).");
          return;
      }
      setNewExpense({
          bankId: bankRow.id,
          date: bankRow.date,
          desc: bankRow.desc,
          amount: Math.abs(bankRow.amount).toFixed(2),
          vendorName: '',
          category: ''
      });
      setAddExpenseOpen(true);
  };

  // --- 7B. QUICK TRANSFER ---
  const openTransferDialog = (e, bankRow) => {
    e.stopPropagation();
    if (bankRow.amount > 0) {
        alert("Transfers Out must be negative on this statement. (Use Income Entry for Transfers In)");
        return;
    }
    setTransferData({
        bankId: bankRow.id,
        date: bankRow.date,
        amount: Math.abs(bankRow.amount).toFixed(2),
        description: `Transfer to ...`,
        targetAccount: ''
    });
    setTransferOpen(true);
  };

  const handleSaveTransfer = async () => {
    try {
        if (!transferData.targetAccount) return alert("Please specify where the money went.");

        const batch = writeBatch(db);
        
        const newExpRef = doc(collection(db, 'churches', churchId, 'financials', 'data', 'expenses'));
        batch.set(newExpRef, {
            date: transferData.date,
            description: `Transfer to ${transferData.targetAccount}`,
            amount: parseFloat(transferData.amount), 
            status: 'approved',
            vendorName: 'Internal Transfer',
            category: 'Bank Transfer',
            paymentMethod: 'Electronic',
            reconciled: true,
            isTransfer: true, 
            reconciledAt: serverTimestamp(),
            reconciledBy: currentUser.uid,
            createdBy: currentUser.uid,
            createdAt: serverTimestamp(),
            audit_matchedBankDate: transferData.date,
            audit_matchedBankDesc: transferData.description
        });

        const hideRef = doc(collection(db, 'churches', churchId, 'financials', 'data', 'hidden_bank_items'));
        batch.set(hideRef, {
            date: transferData.date,
            desc: transferData.description,
            amount: -parseFloat(transferData.amount),
            reason: 'reconciled_via_transfer',
            linkedSystemIds: [newExpRef.id],
            reconciledAt: serverTimestamp(),
            reconciledBy: currentUser.uid
        });

        await batch.commit();

        setBankRows(prev => prev.map(r => r.id === transferData.bankId ? { ...r, permanentlyHidden: true } : r));
        
        const newRow = {
            id: newExpRef.id, 
            date: transferData.date, 
            desc: `Transfer to ${transferData.targetAccount}`, 
            amount: -parseFloat(transferData.amount), 
            type: 'expense', 
            method: 'Electronic', 
            isReconciled: true,
            isTransfer: true 
        };
        setBookRows(prev => [...prev, newRow].sort((a,b) => new Date(a.date) - new Date(b.date)));
        
        setTransferOpen(false);
        setHiddenCount(prev => prev + 1);
        alert("Transfer recorded! Fund balances were NOT changed.");

    } catch (e) { alert("Error recording transfer: " + e.message); }
  };

  const handleSaveExpense = async () => {
      try {
          if (!newExpense.vendorName || !newExpense.category) {
              alert("Please enter a Vendor and Category.");
              return;
          }
          const existingVendor = vendors.find(v => v.name.toLowerCase() === newExpense.vendorName.toLowerCase());
          const vendorId = existingVendor ? existingVendor.id : null;
          const batch = writeBatch(db);

          const newExpRef = doc(collection(db, 'churches', churchId, 'financials', 'data', 'expenses'));
          batch.set(newExpRef, {
              date: newExpense.date,
              description: newExpense.desc,
              amount: parseFloat(newExpense.amount), 
              status: 'approved',
              vendorId: vendorId || 'unknown',
              vendorName: newExpense.vendorName,
              category: newExpense.category,
              paymentMethod: 'Bank Transaction',
              reconciled: true,
              reconciledAt: serverTimestamp(),
              reconciledBy: currentUser.uid,
              createdBy: currentUser.uid,
              createdAt: serverTimestamp(),
              audit_matchedBankDate: newExpense.date,
              audit_matchedBankDesc: newExpense.desc
          });

          const hideRef = doc(collection(db, 'churches', churchId, 'financials', 'data', 'hidden_bank_items'));
          batch.set(hideRef, {
              date: newExpense.date,
              desc: newExpense.desc,
              amount: -parseFloat(newExpense.amount),
              reason: 'reconciled_via_quick_add',
              linkedSystemIds: [newExpRef.id],
              reconciledAt: serverTimestamp(),
              reconciledBy: currentUser.uid
          });

          await batch.commit();
          setBankRows(prev => prev.map(r => r.id === newExpense.bankId ? { ...r, permanentlyHidden: true } : r));
          
          const newRow = {
              id: newExpRef.id, date: newExpense.date, desc: newExpense.vendorName, amount: -parseFloat(newExpense.amount), type: 'expense', method: 'Bank Transaction', auditDesc: newExpense.desc, isReconciled: true
          };
          setBookRows(prev => [...prev, newRow].sort((a,b) => new Date(a.date) - new Date(b.date)));
          setAddExpenseOpen(false);
          setHiddenCount(prev => prev + 1);
      } catch (e) { alert("Error creating expense: " + e.message); }
  };

  // --- 8. LINK & AUTO SCAN ---
  const selectedBankRows = bankRows.filter(r => selectedBankIds.includes(r.id));
  const selectedBookItems = bookRows.filter(r => selectedBookIds.includes(r.id));
  const activeBankTotal = selectedBankRows.reduce((s, i) => s + i.amount, 0);
  const activeBookTotal = selectedBookItems.reduce((s, i) => s + i.amount, 0);
  const canLink = selectedBankRows.length > 0 && selectedBookItems.length > 0 && Math.abs(activeBankTotal - activeBookTotal) < 0.02;

  const handleLink = async () => {
    if (!canLink) return;
    try {
        const batch = writeBatch(db);
        setBankRows(prev => prev.map(r => selectedBankIds.includes(r.id) ? { ...r, matched: true } : r));
        
        const auditDate = selectedBankRows[0].date; 
        const auditDesc = selectedBankRows.length > 1 ? `Multi-Match (${selectedBankRows.length}): ` + selectedBankRows.map(r => r.desc).join(' + ') : selectedBankRows[0].desc;

        selectedBookItems.forEach(item => {
            const collectionName = item.type === 'income' ? 'transactions' : 'expenses';
            const ref = doc(db, 'churches', churchId, 'financials', 'data', collectionName, item.id);
            batch.update(ref, { reconciled: true, reconciledAt: serverTimestamp(), reconciledBy: currentUser.uid, audit_matchedBankDate: auditDate, audit_matchedBankDesc: auditDesc });
        });

        selectedBankRows.forEach(bankRow => {
            const hideRef = doc(collection(db, 'churches', churchId, 'financials', 'data', 'hidden_bank_items'));
            batch.set(hideRef, { date: bankRow.date, desc: bankRow.desc, amount: bankRow.amount, reason: 'reconciled', linkedSystemIds: selectedBookIds, reconciledAt: serverTimestamp(), reconciledBy: currentUser.uid });
        });

        await batch.commit();
        setBookRows(prev => prev.filter(r => !selectedBookIds.includes(r.id)));
        setSelectedBankIds([]); setSelectedBookIds([]);
    } catch (e) { alert("Error linking: " + e.message); }
  };

  const applySuggestion = async (suggestion) => {
      try {
          const batch = writeBatch(db);
          const bankIds = suggestion.bankRows.map(r => r.id);
          setBankRows(prev => prev.map(r => bankIds.includes(r.id) ? { ...r, matched: true } : r));
          const bookIdsToRemove = suggestion.books.map(b => b.id);
          setBookRows(prev => prev.filter(r => !bookIdsToRemove.includes(r.id)));
          const auditDate = suggestion.bankRows[0].date;
          const auditDesc = suggestion.bankRows.length > 1 ? `Multi-Match: ` + suggestion.bankRows.map(r => r.desc).join(' + ') : suggestion.bankRows[0].desc;

          suggestion.books.forEach(item => {
              const collectionName = item.type === 'income' ? 'transactions' : 'expenses';
              const ref = doc(db, 'churches', churchId, 'financials', 'data', collectionName, item.id);
              batch.update(ref, { reconciled: true, reconciledAt: serverTimestamp(), reconciledBy: currentUser.uid, audit_matchedBankDate: auditDate, audit_matchedBankDesc: auditDesc, note: (item.desc || "") + ` [${suggestion.type}]` });
          });
          
          suggestion.bankRows.forEach(bankRow => {
              const hideRef = doc(collection(db, 'churches', churchId, 'financials', 'data', 'hidden_bank_items'));
              batch.set(hideRef, { date: bankRow.date, desc: bankRow.desc, amount: bankRow.amount, reason: 'reconciled_auto', linkedSystemIds: bookIdsToRemove, reconciledAt: serverTimestamp() });
          });

          await batch.commit();
          setSuggestions(prev => prev.filter(s => s.bankRows[0].id !== suggestion.bankRows[0].id));
      } catch (e) { alert("Error applying match: " + e.message); }
  };

  const runAutoScan = () => {
      const newSuggestions = [];
      const usedBookIds = new Set(); 
      const activeBankRows = bankRows.filter(r => !r.matched && !r.alreadyReconciled && !r.permanentlyHidden);

      // A. Check Number Match OR Exact Name/Amount Match (1-to-1)
      activeBankRows.forEach(bankRow => {
          const inGroup = newSuggestions.some(s => s.bankRows.some(br => br.id === bankRow.id));
          if (inGroup) return;

          // Attempt Check Number matching first
          let bankCheckNum = bankRow.checkNumber ? String(bankRow.checkNumber).replace(/^0+/, '') : null;
          if (!bankCheckNum) {
              const checkMatch = (bankRow.desc || '').toLowerCase().match(/check\s*#?\s*0*(\d+)/i);
              if (checkMatch && checkMatch[1]) bankCheckNum = checkMatch[1];
          }

          let exactMatch = null;
          let matchType = 'Exact Match';

          if (bankCheckNum) {
              exactMatch = bookRows.find(b => {
                  if (usedBookIds.has(b.id)) return false;
                  if (!b.checkNumber) return false;
                  return String(b.checkNumber).replace(/^0+/, '') === bankCheckNum && Math.abs(b.amount - bankRow.amount) < 0.02;
              });
              if (exactMatch) matchType = 'Check Match';
          }

          // Fallback to standard exact match
          if (!exactMatch) {
              const candidates = bookRows.filter(book => {
                  if (usedBookIds.has(book.id)) return false;
                  const daysDiff = Math.abs((new Date(bankRow.date) - new Date(book.date)) / (1000 * 60 * 60 * 24));
                  return daysDiff <= 4;
              });
              exactMatch = candidates.find(c => Math.abs(c.amount - bankRow.amount) < 0.01 && isNameMatch(bankRow.desc, c.desc));
          }

          if (exactMatch) {
              usedBookIds.add(exactMatch.id);
              newSuggestions.push({ type: matchType, bankRows: [bankRow], books: [exactMatch], confidence: 'High' });
          }
      });

      // B. Merge Match (Many-to-1)
      const bankGroups = {};
      activeBankRows.forEach(row => {
          // Skip if we already matched it via exact/check match
          const inGroup = newSuggestions.some(s => s.bankRows.some(br => br.id === row.id));
          if (inGroup) return;

          const key = normalize(row.desc).substring(0, 15); 
          if (!key) return;
          if (!bankGroups[key]) bankGroups[key] = [];
          bankGroups[key].push(row);
      });

      Object.values(bankGroups).forEach(group => {
          if (group.length < 2) return; 
          const groupSum = group.reduce((s, r) => s + r.amount, 0);
          const lastDate = new Date(group[group.length-1].date);
          const match = bookRows.find(b => {
              if (usedBookIds.has(b.id)) return false;
              if (Math.abs(b.amount - groupSum) > 0.02) return false;
              const bDate = new Date(b.date);
              const daysDiff = Math.abs((lastDate - bDate) / (1000 * 60 * 60 * 24));
              if (daysDiff > 4) return false;
              return isNameMatch(group[0].desc, b.desc);
          });
          if (match) {
              usedBookIds.add(match.id);
              newSuggestions.push({ type: `Merge Match (${group.length} items)`, bankRows: group, books: [match], confidence: 'Medium' });
          }
      });

      setSuggestions(newSuggestions);
      if (newSuggestions.length > 0) setSuggestionDialogOpen(true);
      else alert("No name-verified or check number matches found.");
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={6} md={2}><TextField fullWidth type="date" label="Start" value={startDate} onChange={(e)=>setStartDate(e.target.value)} InputLabelProps={{shrink:true}}/></Grid>
            <Grid item xs={6} md={2}><TextField fullWidth type="date" label="End" value={endDate} onChange={(e)=>setEndDate(e.target.value)} InputLabelProps={{shrink:true}}/></Grid>
            <Grid item xs={12} md={8}>
                <Button variant="contained" onClick={fetchBookData} disabled={loading}>Load Records</Button>
                <Button variant="outlined" component="label" startIcon={<FileUpload />} sx={{ ml: 2 }}>
                  Upload CSV <input type="file" hidden accept=".csv" onChange={handleFileUpload} />
                </Button>
                <Button variant="contained" color="secondary" startIcon={<AutoFixHigh />} sx={{ ml: 2 }} onClick={runAutoScan} disabled={bankRows.length === 0 || bookRows.length === 0}>
                    Auto-Scan Matches
                </Button>
                <FormControlLabel control={<Switch checked={showAll} onChange={(e) => setShowAll(e.target.checked)} color="primary" />} label="Show All System Records" sx={{ ml: 2 }} />
            </Grid>
        </Grid>
        
        {(reconciledCount > 0 || hiddenCount > 0) && <Alert severity="info" sx={{ mt: 2 }} icon={<History />}>Filtered: {reconciledCount} reconciled items, {hiddenCount} manually hidden items.</Alert>}
        
        <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
                {selectedBankIds.length > 0 ? (
                    <Typography variant="body2">
                        Bank ({selectedBankIds.length}): <strong>${activeBankTotal.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong> vs System ({selectedBookIds.length}): <strong>${activeBookTotal.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong>
                    </Typography>
                ) : (
                    <Typography variant="body2" color="textSecondary">Select items from both sides to match.</Typography>
                )}
            </Box>
            <Button variant="contained" color="success" disabled={!canLink} onClick={handleLink} startIcon={<Link />}>
                Link Match
            </Button>
        </Box>
      </Paper>

      <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" align="center" sx={{ bgcolor: '#eee', p: 1 }}>Bank Statement</Typography>
                  <TableContainer sx={{ maxHeight: '65vh' }}>
                      <Table size="small" stickyHeader>
                          <TableHead><TableRow><TableCell></TableCell><TableCell>Date</TableCell><TableCell>Desc</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
                          <TableBody>
                              {sortedBankRows.filter(r => !r.matched && !r.alreadyReconciled && !r.permanentlyHidden).map((row) => (
                                  <TableRow key={row.id} hover onClick={() => handleBankRowClick(row.id)} selected={selectedBankIds.includes(row.id)} sx={{ cursor: 'pointer' }}>
                                      <TableCell padding="none">
                                          <Tooltip title="Hide Permanently">
                                              <IconButton size="small" color="default" onClick={(e) => hideBankRow(e, row.id)} sx={{ opacity: 0.5 }}>
                                                  <VisibilityOff fontSize="small" />
                                              </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Quick Add Expense">
                                              <IconButton size="small" color="primary" onClick={(e) => openAddExpenseDialog(e, row)} sx={{ opacity: 0.8 }}>
                                                  <AddCircle fontSize="small" />
                                              </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Record Transfer (No Fund Impact)">
                                              <IconButton size="small" color="secondary" onClick={(e) => openTransferDialog(e, row)} sx={{ opacity: 0.8 }}>
                                                  <SwapHoriz fontSize="small" />
                                              </IconButton>
                                          </Tooltip>
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.75rem' }}>{row.date}</TableCell>
                                      <TableCell sx={{ fontSize: '0.75rem' }}>{row.desc?.substring(0, 30)}...</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold', color: row.amount < 0 ? 'error.main' : 'success.main' }}>
                                          {row.amount.toLocaleString(undefined, {minimumFractionDigits: 2})}
                                      </TableCell>
                                  </TableRow>
                              ))}
                          </TableBody>
                      </Table>
                  </TableContainer>
              </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" align="center" sx={{ bgcolor: showAll ? '#fff3e0' : '#e3f2fd', p: 1 }}>System Records</Typography>
                  <TableContainer sx={{ maxHeight: '65vh' }}>
                      <Table size="small" stickyHeader>
                          <TableHead><TableRow><TableCell></TableCell><TableCell>Date</TableCell><TableCell>Entity</TableCell><TableCell>Method</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
                          <TableBody>
                              {sortedBookRows.map(row => (
                                  <TableRow key={row.id} hover onClick={() => toggleBookRow(row.id)} selected={selectedBookIds.includes(row.id)} sx={{ cursor: 'pointer', bgcolor: getMatchIndicator(row) ? '#f1f8e9' : (row.isReconciled ? '#fff3e0' : 'inherit') }}>
                                      <TableCell padding="checkbox"><Checkbox checked={selectedBookIds.includes(row.id)} size="small" /></TableCell>
                                      <TableCell sx={{ fontSize: '0.75rem' }}>{row.date}</TableCell>
                                      <TableCell sx={{ fontSize: '0.75rem' }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                              {getMatchIndicator(row)}
                                              {row.desc?.substring(0, 20)}
                                              {/* NEW: Displays the check number directly in the row */}
                                              {row.checkNumber && <Chip label={`#${row.checkNumber}`} size="small" sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }} />}
                                              {row.isReconciled && <Chip label="Done" size="small" sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }} />}
                                              {row.isTransfer && <Chip label="Transfer" size="small" color="info" sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }} />}
                                          </Box>
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.75rem' }}>{row.method}</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold', color: row.amount < 0 ? 'error.main' : 'success.main' }}>
                                          {row.amount.toLocaleString(undefined, {minimumFractionDigits: 2})}
                                      </TableCell>
                                  </TableRow>
                              ))}
                          </TableBody>
                      </Table>
                  </TableContainer>
              </Paper>
          </Grid>
      </Grid>

      {/* QUICK ADD EXPENSE */}
      <Dialog open={addExpenseOpen} onClose={() => setAddExpenseOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Quick Add Expense</DialogTitle>
          <DialogContent>
              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
                  Create a new expense record from this bank transaction.
              </Typography>
              <Grid container spacing={2}>
                  <Grid item xs={6}><TextField label="Date" fullWidth value={newExpense.date} onChange={(e) => setNewExpense({...newExpense, date: e.target.value})} /></Grid>
                  <Grid item xs={6}><TextField label="Amount" fullWidth value={newExpense.amount} disabled /></Grid>
                  <Grid item xs={12}><TextField label="Description" fullWidth value={newExpense.desc} onChange={(e) => setNewExpense({...newExpense, desc: e.target.value})} /></Grid>
                  <Grid item xs={12}>
                      <Autocomplete
                          freeSolo options={vendors.map((v) => v.name)}
                          value={newExpense.vendorName}
                          onChange={(e, v) => setNewExpense({ ...newExpense, vendorName: v })}
                          onInputChange={(e, v) => setNewExpense({ ...newExpense, vendorName: v })}
                          renderInput={(p) => <TextField {...p} label="Vendor" fullWidth />}
                      />
                  </Grid>
                  <Grid item xs={12}>
                      <Autocomplete
                          freeSolo options={categories.map((c) => c.name)}
                          value={newExpense.category}
                          onChange={(e, v) => setNewExpense({ ...newExpense, category: v })}
                          onInputChange={(e, v) => setNewExpense({ ...newExpense, category: v })}
                          renderInput={(p) => <TextField {...p} label="Category" fullWidth />}
                      />
                  </Grid>
              </Grid>
          </DialogContent>
          <DialogActions>
              <Button onClick={() => setAddExpenseOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveExpense} variant="contained" color="primary">Create & Reconcile</Button>
          </DialogActions>
      </Dialog>

      {/* QUICK TRANSFER DIALOG */}
      <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SwapHoriz color="secondary" /> Record Internal Transfer
          </DialogTitle>
          <DialogContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                  This creates a record to match your bank statement but <strong>will not deduct money</strong> from your Ministry Funds.
              </Alert>
              <Grid container spacing={2}>
                  <Grid item xs={6}><TextField label="Date" fullWidth value={transferData.date} onChange={(e) => setTransferData({...transferData, date: e.target.value})} /></Grid>
                  <Grid item xs={6}><TextField label="Amount" fullWidth value={transferData.amount} disabled /></Grid>
                  <Grid item xs={12}>
                      <TextField 
                        label="Transfer To (Target Bank/Account)" 
                        fullWidth 
                        placeholder="e.g. Savings Account, Building Fund Account"
                        value={transferData.targetAccount} 
                        onChange={(e) => setTransferData({...transferData, targetAccount: e.target.value})} 
                      />
                  </Grid>
              </Grid>
          </DialogContent>
          <DialogActions>
              <Button onClick={() => setTransferOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveTransfer} variant="contained" color="secondary">Record Transfer</Button>
          </DialogActions>
      </Dialog>

      {/* AUTO MATCH SUGGESTION DIALOG */}
      <Dialog open={suggestionDialogOpen} onClose={() => setSuggestionDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Verified Matches ({suggestions.length})</DialogTitle>
          <DialogContent dividers>
              <List>
                  {suggestions.map((s, idx) => (
                      <React.Fragment key={idx}>
                          <ListItem alignItems="flex-start" secondaryAction={<Button variant="contained" size="small" onClick={() => applySuggestion(s)}>Confirm</Button>}>
                              <ListItemText 
                                primary={
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Typography fontWeight="bold">${s.bankRows.reduce((a, b) => a + b.amount, 0).toFixed(2)}</Typography>
                                        <Chip label={s.type} size="small" color={s.type.includes('Merge') ? "warning" : (s.type.includes('Check') ? "info" : "success")} />
                                    </Box>
                                }
                                secondary={
                                    <Box component="span">
                                        <Typography variant="caption" display="block">Bank: {s.bankRows.map(r => r.desc).join(' + ')}</Typography>
                                        <Typography variant="caption" display="block">System: {s.books[0].desc}</Typography>
                                    </Box>
                                }
                              />
                          </ListItem>
                          <Divider component="li" />
                      </React.Fragment>
                  ))}
              </List>
          </DialogContent>
          <DialogActions><Button onClick={() => setSuggestionDialogOpen(false)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankReconciliation;